"use client";

import { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import { LuCheck, LuMoon, LuSunMedium } from "react-icons/lu";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

type ThemeChoice = "light" | "dark";

const THEME_OPTIONS: readonly {
  readonly value: ThemeChoice;
  readonly label: string;
  readonly Icon: typeof LuSunMedium;
}[] = [
  { value: "light", label: "Light", Icon: LuSunMedium },
  { value: "dark", label: "Dark", Icon: LuMoon },
];

function resolveActiveTheme(
  theme: string | undefined,
  resolvedTheme: string | undefined,
): ThemeChoice {
  if (theme === "light" || theme === "dark") {
    return theme;
  }
  return resolvedTheme === "light" ? "light" : "dark";
}

/** Icon button showing the active theme; dropdown switches Light / Dark. */
export function ModeToggle({
  className,
}: {
  readonly className?: string;
}): JSX.Element {
  const { theme, resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const active = resolveActiveTheme(theme, resolvedTheme);
  const ActiveIcon = active === "light" ? LuSunMedium : LuMoon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={cn(
            "size-8 shrink-0 border-border bg-background/90 shadow-sm backdrop-blur-sm",
            className,
          )}
          title={`Theme: ${active === "light" ? "Light" : "Dark"}`}
          aria-label="Change theme"
        >
          {mounted ? (
            <ActiveIcon className="size-4" />
          ) : (
            <LuMoon className="size-4" />
          )}
          <span className="sr-only">Change theme</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {THEME_OPTIONS.map(({ value, label, Icon }) => (
          <DropdownMenuItem
            key={value}
            className="cursor-pointer"
            onClick={() => {
              setTheme(value);
            }}
          >
            <Icon className="mr-2 size-4" />
            <span className="flex-1">{label}</span>
            <LuCheck
              className={cn(
                "ml-2 size-4",
                active === value ? "opacity-100" : "opacity-0",
              )}
            />
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
